import type { ConventionReportSummary } from '@/services/useConventionReport';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Tooltip, ResponsiveContainer, BarChart, Bar, XAxis, YAxis, CartesianGrid, LabelList, Legend } from 'recharts';

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884d8', '#82ca9d'];

interface ParticipantSectionProps {
  data: ConventionReportSummary;
  onPackageClick?: (packageId: string) => void;
}

export function ParticipantSection({ data, onPackageClick }: ParticipantSectionProps) {
  const { participants } = data;

  const packageData = participants.per_package.map(p => ({
    name: p.package_name,
    value: p.total,
  }));

  const genderData = [
    { name: 'Laki-laki', value: participants.per_gender.male || 0 },
    { name: 'Perempuan', value: participants.per_gender.female || 0 },
  ].filter(d => d.value > 0);

  const institutionData = participants.per_institution.map(i => ({
    name: i.institution,
    total: i.total,
    paid: i.paid,
  }));

  return (
    <div className="space-y-6">
      {/* Kartu Paket Peserta */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        {participants.per_package.map((p, i) => (
          <div 
            key={p.package_id} 
            onClick={() => onPackageClick && onPackageClick(String(p.package_id))}
            className="bg-white dark:bg-slate-900 p-5 rounded-2xl shadow-sm border border-slate-100 dark:border-slate-800 cursor-pointer hover:shadow-md hover:-translate-y-1 transition-all duration-300 relative overflow-hidden group flex flex-col justify-between min-h-[110px]"
          >
            <div 
              className="absolute top-0 right-0 w-24 h-24 rounded-full -mr-8 -mt-8 opacity-10 group-hover:opacity-20 transition-opacity"
              style={{ backgroundColor: COLORS[i % COLORS.length] }}
            />
            <div className="flex items-start gap-2 mb-2 z-10">
              <div className="w-3 h-3 rounded-full mt-1 shrink-0" style={{ backgroundColor: COLORS[i % COLORS.length] }} />
              <p className="text-[11px] font-bold text-slate-500 uppercase tracking-widest leading-tight" title={p.package_name}>{p.package_name}</p>
            </div>
            <p className="text-3xl font-black text-slate-900 z-10">{p.total}</p>
            <div className="text-[10px] text-slate-400 font-medium mt-1 z-10">
              Lunas: <span className="text-emerald-500 font-bold">{p.paid}</span> | Menunggu: <span className="text-amber-500 font-bold">{p.pending}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Distribusi Paket */}
        <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-gradient-to-br from-indigo-50/50 to-purple-50/50 dark:from-indigo-900/10 dark:to-purple-900/10 border-indigo-100 dark:border-indigo-900/30">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Berdasarkan Paket</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4 pt-2">
              {packageData.map((pkg, idx) => {
                const percentage = participants.total > 0 ? (pkg.value / participants.total) * 100 : 0;
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <span className="font-medium text-slate-700">{pkg.name}</span>
                      <span className="font-bold text-slate-900">{pkg.value} <span className="text-slate-400 font-normal text-xs ml-1">({percentage.toFixed(1)}%)</span></span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: COLORS[idx % COLORS.length]
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>

        {/* Distribusi Gender */}
        <Card className="overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-gradient-to-br from-emerald-50/50 to-teal-50/50 dark:from-emerald-900/10 dark:to-teal-900/10 border-emerald-100 dark:border-emerald-900/30">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Distribusi Gender</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6 pt-2">
              {genderData.map((gender, idx) => {
                const totalGender = genderData.reduce((acc, curr) => acc + curr.value, 0);
                const percentage = totalGender > 0 ? (gender.value / totalGender) * 100 : 0;
                return (
                  <div key={idx} className="space-y-2">
                    <div className="flex justify-between items-center text-sm">
                      <div className="flex items-center gap-2">
                        <div 
                          className="w-3 h-3 rounded-full" 
                          style={{ backgroundColor: COLORS[(idx + 2) % COLORS.length] }} 
                        />
                        <span className="font-medium text-slate-700">{gender.name}</span>
                      </div>
                      <span className="font-bold text-slate-900">{gender.value} <span className="text-slate-400 font-normal text-xs ml-1">({percentage.toFixed(1)}%)</span></span>
                    </div>
                    <div className="w-full bg-slate-100 dark:bg-slate-800 rounded-full h-2.5">
                      <div
                        className="h-2.5 rounded-full"
                        style={{
                          width: `${percentage}%`,
                          backgroundColor: COLORS[(idx + 2) % COLORS.length]
                        }}
                      />
                    </div>
                  </div>
                );
              })}
            </div>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {/* Top 10 Institusi */}
        <Card className="md:col-span-2 overflow-hidden border-0 shadow-sm hover:shadow-md transition-all duration-300 bg-gradient-to-br from-sky-50/50 to-blue-50/50 dark:from-sky-900/10 dark:to-blue-900/10 border-sky-100 dark:border-sky-900/30">
          <CardHeader>
            <CardTitle className="text-sm font-medium">Semua Institusi Pendaftar</CardTitle>
          </CardHeader>
          <CardContent className="h-[500px] overflow-y-auto custom-scrollbar">
            <div style={{ height: Math.max(500, institutionData.length * 60) }} className="w-full">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={institutionData}
                  layout="vertical"
                  margin={{ top: 5, right: 60, left: 100, bottom: 5 }}
                >
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis dataKey="name" type="category" width={150} tick={{ fontSize: 12 }} />
                <Tooltip />
                <Legend />
                <Bar dataKey="total" name="Total Pendaftar" fill="#8884d8">
                  <LabelList dataKey="total" position="right" style={{ fontSize: '11px', fontWeight: 'bold' }} />
                </Bar>
                <Bar dataKey="paid" name="Sudah Lunas" fill="#82ca9d">
                  <LabelList dataKey="paid" position="right" style={{ fontSize: '11px', fontWeight: 'bold' }} />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
